// Animation utilities for smooth transitions and effects
export interface AnimationConfig {
    duration: number;
    easing: string;
    delay?: number;
    fillMode?: 'forwards' | 'backwards' | 'both' | 'none';
}

export const ANIMATION_PRESETS = {
    fadeIn: {
        duration: 300,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fillMode: 'forwards' as const
    },
    slideUp: {
        duration: 400,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fillMode: 'forwards' as const
    },
    slideDown: {
        duration: 400,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fillMode: 'forwards' as const
    },
    scaleIn: {
        duration: 300,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        fillMode: 'forwards' as const
    },
    bounce: {
        duration: 600,
        easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        fillMode: 'forwards' as const
    },
    pulse: {
        duration: 2000,
        easing: 'ease-in-out',
        fillMode: 'both' as const
    }
};

// Animation keyframes
export const KEYFRAMES = {
    fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 }
    },
    fadeInUp: {
        from: { 
            opacity: 0, 
            transform: 'translateY(20px)' 
        },
        to: { 
            opacity: 1, 
            transform: 'translateY(0)' 
        }
    },
    fadeOut: {
        from: { opacity: 1 },
        to: { opacity: 0 }
    },
    slideUp: {
        from: { 
            opacity: 0, 
            transform: 'translateY(20px) scale(0.95)' 
        },
        to: { 
            opacity: 1, 
            transform: 'translateY(0) scale(1)' 
        }
    },
    slideDown: {
        from: { 
            opacity: 0, 
            transform: 'translateY(-20px) scale(0.95)' 
        },
        to: { 
            opacity: 1, 
            transform: 'translateY(0) scale(1)' 
        }
    },
    slideLeft: {
        from: { 
            opacity: 0, 
            transform: 'translateX(20px) scale(0.95)' 
        },
        to: { 
            opacity: 1, 
            transform: 'translateX(0) scale(1)' 
        }
    },
    slideRight: {
        from: { 
            opacity: 0, 
            transform: 'translateX(-20px) scale(0.95)' 
        },
        to: { 
            opacity: 1, 
            transform: 'translateX(0) scale(1)' 
        }
    },
    scaleIn: {
        from: { 
            opacity: 0, 
            transform: 'scale(0.8)' 
        },
        to: { 
            opacity: 1, 
            transform: 'scale(1)' 
        }
    },
    scaleOut: {
        from: { 
            opacity: 1, 
            transform: 'scale(1)' 
        },
        to: { 
            opacity: 0, 
            transform: 'scale(0.8)' 
        }
    },
    bounce: {
        '0%': { 
            opacity: 0, 
            transform: 'scale(0.3)' 
        },
        '50%': { 
            opacity: 1, 
            transform: 'scale(1.05)' 
        },
        '70%': { 
            transform: 'scale(0.9)' 
        },
        '100%': { 
            opacity: 1, 
            transform: 'scale(1)' 
        }
    },
    pulse: {
        '0%': { 
            transform: 'scale(1)' 
        },
        '50%': { 
            transform: 'scale(1.05)' 
        },
        '100%': { 
            transform: 'scale(1)' 
        }
    },
    shimmer: [
        { backgroundPosition: '-200% 0' },
        { backgroundPosition: '200% 0' }
    ],
    spin: [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(360deg)' }
    ]
};

// Animation utility functions
export const animateElement = (
    element: HTMLElement,
    keyframes: Record<string, any>,
    config: AnimationConfig
): Animation => {
    return element.animate(keyframes, {
        duration: config.duration,
        easing: config.easing,
        delay: config.delay || 0,
        fill: config.fillMode || 'forwards'
    });
};

export const fadeIn = (element: HTMLElement, duration: number = 300): Animation => {
    return animateElement(element, KEYFRAMES.fadeIn, {
        ...ANIMATION_PRESETS.fadeIn,
        duration
    });
};

export const slideUp = (element: HTMLElement, duration: number = 400): Animation => {
    return animateElement(element, KEYFRAMES.slideUp, {
        ...ANIMATION_PRESETS.slideUp,
        duration
    });
};

export const slideDown = (element: HTMLElement, duration: number = 400): Animation => {
    return animateElement(element, KEYFRAMES.slideDown, {
        ...ANIMATION_PRESETS.slideDown,
        duration
    });
};

export const scaleIn = (element: HTMLElement, duration: number = 300): Animation => {
    return animateElement(element, KEYFRAMES.scaleIn, {
        ...ANIMATION_PRESETS.scaleIn,
        duration
    });
};

export const bounce = (element: HTMLElement, duration: number = 600): Animation => {
    return animateElement(element, KEYFRAMES.bounce, {
        ...ANIMATION_PRESETS.bounce,
        duration
    });
};

// Stagger animation for multiple elements
export const staggerAnimation = (
    elements: HTMLElement[],
    animationFn: (element: HTMLElement) => Animation,
    staggerDelay: number = 100
): Promise<Animation[]> => {
    const animations: Animation[] = [];
    
    elements.forEach((element, index) => {
        setTimeout(() => {
            const animation = animationFn(element);
            animations.push(animation);
        }, index * staggerDelay);
    });
    
    return Promise.all(animations);
};

// Intersection Observer for scroll animations
export const createScrollAnimations = () => {
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const element = entry.target as HTMLElement;
                    const animationType = element.dataset.animation || 'fadeIn';
                    
                    switch (animationType) {
                        case 'fadeIn':
                            fadeIn(element);
                            break;
                        case 'slideUp':
                            slideUp(element);
                            break;
                        case 'slideDown':
                            slideDown(element);
                            break;
                        case 'scaleIn':
                            scaleIn(element);
                            break;
                        case 'bounce':
                            bounce(element);
                            break;
                    }
                    
                    observer.unobserve(element);
                }
            });
        },
        {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        }
    );
    
    return observer;
};

// Loading animation utilities
export const createLoadingAnimation = (element: HTMLElement): Animation => {
    return element.animate(KEYFRAMES.spin, {
        duration: 1000,
        iterations: Infinity,
        easing: 'linear'
    });
};

export const createShimmerEffect = (element: HTMLElement): Animation => {
    element.style.background = 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)';
    element.style.backgroundSize = '200% 100%';
    
    return element.animate(KEYFRAMES.shimmer, {
        duration: 1500,
        iterations: Infinity,
        easing: 'linear'
    });
};

// Page transition utilities
export const pageTransition = {
    enter: (element: HTMLElement) => {
        element.style.opacity = '0';
        element.style.transform = 'translateY(20px)';
        
        requestAnimationFrame(() => {
            element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
        });
    },
    
    exit: (element: HTMLElement) => {
        element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.opacity = '0';
        element.style.transform = 'translateY(-20px)';
    }
};

// Reduced motion support
export const respectsReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Safe animation that respects user preferences
export const safeAnimate = (
    element: HTMLElement,
    keyframes: Record<string, any>,
    config: AnimationConfig
): Animation | null => {
    if (respectsReducedMotion()) {
        // Apply final state immediately for reduced motion
        const finalFrame = keyframes.to || keyframes['100%'] || keyframes;
        Object.assign(element.style, finalFrame);
        return null;
    }
    
    return animateElement(element, keyframes, config);
};

const animationUtils = {
    animateElement,
    fadeIn,
    slideUp,
    slideDown,
    scaleIn,
    bounce,
    staggerAnimation,
    createScrollAnimations,
    createLoadingAnimation,
    createShimmerEffect,
    pageTransition,
    respectsReducedMotion,
    safeAnimate
};

export default animationUtils;
